import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useQueueMonitor } from '../../hooks/useQueueMonitor';
import { formatTime, getTimeElapsed, obscureEmail, obscurePhone } from '../../utils/helpers';
import { queueAPI } from '../../services/api';
import WaiverModal from './WaiverModal';
import LanguageSwitcher from '../common/LanguageSwitcher';
import './QueueMonitor.css';

const HOLD_DURATION = 3000;

const QueueMonitor = () => {
  const { workstationInfo, logout } = useAuth();
  const { queue, loading, error, lastUpdate, refresh } = useQueueMonitor(5000);
  const { t } = useTranslation(['queue', 'common']);
  const [selectedWaiver, setSelectedWaiver] = useState(null);
  const [holdingItemId, setHoldingItemId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const holdTimerRef = useRef(null);
  const holdFiredRef = useRef(false);

  const handleRowClick = (item) => {
    setSelectedWaiver(item);
  };

  const handleCloseModal = () => {
    setSelectedWaiver(null);
    refresh(); // Refresh queue after closing modal
  };

  const startHold = (item) => {
    holdFiredRef.current = false;
    setHoldingItemId(item.queue_id);
    holdTimerRef.current = setTimeout(() => {
      holdFiredRef.current = true;
      setHoldingItemId(null);
      setCancelTarget(item);
    }, HOLD_DURATION);
  };

  const cancelHold = () => {
    clearTimeout(holdTimerRef.current);
    setHoldingItemId(null);
  };

  const handleItemClick = (item) => {
    if (holdFiredRef.current) {
      holdFiredRef.current = false;
      return;
    }
    handleRowClick(item);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await queueAPI.updateQueueItem(cancelTarget.queue_id, { status: 'cancelled' });
      setCancelTarget(null);
      refresh();
    } catch (err) {
      console.error('Failed to cancel item:', err);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="queue-monitor">
      {/* Header */}
      <header className="queue-header">
        <div className="header-left">
          <h1>{t('title', { ns: 'queue' })}</h1>
          <div className="workstation-info">
            <span className="tenant-name">{workstationInfo?.tenant_name}</span>
            <span className="workstation-name">{workstationInfo?.location_name}</span>
            <span className="workstation-location">{workstationInfo?.terminal_name}</span>
          </div>
        </div>
        <div className="header-right">
          <LanguageSwitcher />
          <span className="header-queue-count">{queue.filter((item) => item.status !== 'in_progress').length} {t('pending', { ns: 'queue' })}</span>
          {lastUpdate && (
            <span className="last-update">
              {t('updated', { ns: 'queue' })} {formatTime(lastUpdate)}
            </span>
          )}
          <button className="refresh-btn" onClick={refresh} disabled={loading} title="Refresh">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
      </header>

      {/* Queue Content */}
      <main className="queue-content">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {loading && queue.length === 0 ? (
          <div className="queue-loading">
            <div className="spinner-large"></div>
            <p>{t('loading', { ns: 'queue' })}</p>
          </div>
        ) : queue.filter((item) => item.status !== 'in_progress').length === 0 ? (
          <div className="queue-empty">
            <div className="empty-icon">📋</div>
            <h2>{t('empty.title', { ns: 'queue' })}</h2>
            <p>{t('empty.description', { ns: 'queue' })}</p>
          </div>
        ) : (
          <>
            <div className="queue-list">
              {queue.filter((item) => item.status !== 'in_progress').map((item) => (
                <div
                  key={item.queue_id}
                  className={`queue-item${holdingItemId === item.queue_id ? ' queue-item-holding' : ''}`}
                  onClick={() => handleItemClick(item)}
                  onMouseDown={() => startHold(item)}
                  onMouseUp={cancelHold}
                  onMouseLeave={cancelHold}
                  onTouchStart={() => startHold(item)}
                  onTouchEnd={cancelHold}
                  onTouchCancel={cancelHold}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <div className="queue-item-header">
                    <div className="queue-item-header-left">
                      <span className="customer-name">{item.guest_name}</span>
                      {item.party_size > 1 && (
                        <span className="party-size-chip">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          {t('partyOf', { ns: 'queue', count: item.party_size })}
                        </span>
                      )}
                    </div>
                    <span className="queue-time">{getTimeElapsed(item.created_at)}</span>
                  </div>
                  <div className="queue-item-body">
                    <div className="queue-detail">
                      <span className="detail-label">{t('email', { ns: 'queue' })}</span>
                      <span className="detail-value">{obscureEmail(item.guest_email) || t('na', { ns: 'common' })}</span>
                    </div>
                    <div className="queue-detail">
                      <span className="detail-label">{t('phone', { ns: 'queue' })}</span>
                      <span className="detail-value">{obscurePhone(item.guest_phone) || t('na', { ns: 'common' })}</span>
                    </div>
                    {item.waivers && item.waivers > 1 && (
                      <div className="queue-detail">
                        <span className="detail-label">{t('waivers', { ns: 'queue' })}</span>
                        <span className="detail-value">{item.waivers}</span>
                      </div>
                    )}
                  </div>
                  <div className="queue-item-footer">
                    <span className="click-hint">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                      {t('tapToSign', { ns: 'queue' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Cancel Confirmation Modal */}
      {cancelTarget && (
        <div className="cancel-confirm-overlay">
          <div className="cancel-confirm-dialog">
            <h3 className="cancel-confirm-title">{t('cancel.title', { ns: 'queue' })}</h3>
            <p className="cancel-confirm-message">{t('cancel.message', { ns: 'queue', name: cancelTarget.guest_name })}</p>
            <div className="cancel-confirm-actions">
              <button className="cancel-confirm-keep" onClick={() => setCancelTarget(null)} disabled={cancelling}>
                {t('cancel.keep', { ns: 'queue' })}
              </button>
              <button className="cancel-confirm-remove" onClick={handleCancelConfirm} disabled={cancelling}>
                {cancelling ? t('cancel.removing', { ns: 'queue' }) : t('cancel.remove', { ns: 'queue' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waiver Modal */}
      {selectedWaiver && (
        <WaiverModal
          waiver={selectedWaiver}
          onClose={handleCloseModal}
        />
      )}

      {/* Footer */}
      <footer className="queue-footer">
        <button className="footer-logout-btn" onClick={logout}>
          {t('signOut', { ns: 'queue' })}
        </button>
      </footer>

      {/* Powered by banner */}
      <div className="powered-by-banner">
        <span>{t('poweredBy', { ns: 'common' })}</span>
        <img src="/accesso-logo.webp" alt="accesso" className="powered-by-logo" />
      </div>
    </div>
  );
};

export default QueueMonitor;
